import { FC, useState, useRef, useEffect } from 'react';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useColorScheme,
} from '@mui/material';
import Icon from '@/components/ui/Icon';

import useAuthStore from '@/store/auth';
import useGoogleCalendarStore from '@/store/google';
import { openGoogleAuth } from '@/utils/googleAuth';
import NotificationBell from '@/components/ui/NotificationBell';
import GlobalSearchDropdown from '@/components/GlobalSearchDropdown';
import ProfileDialog from '@/components/modals/ProfileDialog';
import useClearState from '@/hooks/useClearState';
import { KopfBand } from '@/components/ui/Rahmen';
import TabPills from '@/components/ui/shared/TabPills';
import { FARBMODI, Farbmodus } from '@/theme/farbschema';

const getInitials = (name?: string): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const getRoleLabel = (roles?: string[]): string => {
  if (!roles || roles.length === 0) return '';
  // Labels konsistent zu client/src/locales/de-DE/common.json (Sektion roles).
  // Decision #51 hat die Rollen auf SA/VP/CC konsolidiert; vv/vd/vm/bo gibt es
  // nicht mehr. Fallback unten (toUpperCase) greift nur noch bei echten Unbekannten.
  const roleMap: Record<string, string> = {
    sa: 'Super-Admin',
    vp: 'Vertriebspartner',
    cc: 'Callcenter',
  };
  return roleMap[roles[0]] ?? roles[0].toUpperCase();
};

const AppBar: FC = () => {
  const { user, signOut } = useAuthStore();
  const { clearAllStates } = useClearState();
  const { isConnected: googleConnected, googleEmail, checkStatus: checkGoogleStatus, disconnect: googleDisconnect } = useGoogleCalendarStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  // Dark Mode (Decision #248): Hell / Dunkel / System, gespeichert im Browser.
  // `mode` ist beim ersten Rendern schon gesetzt (Provider mit `noSsr`).
  const { mode, setMode } = useColorScheme();

  useEffect(() => {
    checkGoogleStatus();
  }, [checkGoogleStatus]);

  const handleSignOut = () => {
    setMenuOpen(false);
    clearAllStates();
    signOut();
  };

  return (
    <KopfBand>
      <Stack direction="row" flex={1} justifyContent="space-between" alignItems="center" sx={{ px: 8 }}>
        {/* Seitentitel steht wieder im Inhaltsbereich (Korrekturrunde 1, Alex
            26.10.2026) — die Kopfleiste traegt Suche, Glocke und Konto. */}
        <GlobalSearchDropdown />
        <Stack direction="row" alignItems="center" gap={2}>
          <NotificationBell />
          <Avatar
            ref={anchorRef}
            onClick={() => setMenuOpen(true)}
            sx={{
              width: 32,
              height: 32,
              typography: 'label',
              // Stufe 2b: Original-Farben (Teal-Kreis, weisse Initialen).
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'primary.dark' },
              transition: 'background-color 150ms',
            }}
          >
            {getInitials(user?.name)}
          </Avatar>
          <Menu
            anchorEl={anchorRef.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: { mt: 2, minWidth: 200 },
              },
            }}
          >
            <Box sx={{ px: 4, py: 2 }}>
              <Typography variant="kartentitel">
                {user?.name}
              </Typography>
              <Typography variant="klein">
                {getRoleLabel(user?.roles)}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                setProfileOpen(true);
              }}
              sx={{ py: 2, px: 4 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon icon="AccountCircle" size={16} sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="wertBetont">
                  Profil bearbeiten
                </Typography>
              </ListItemText>
            </MenuItem>
            <Box sx={{ px: 4, py: 2 }}>
              <Typography variant="label" component="p" sx={{ mb: 1 }}>
                Darstellung
              </Typography>
              <TabPills
                aria-label="Darstellung"
                variante="segment"
                tabs={FARBMODI.map((m) => ({ key: m.wert, label: m.label }))}
                activeKey={mode ?? 'light'}
                onChange={(wert) => setMode(wert as Farbmodus)}
              />
            </Box>
            <MenuItem
              onClick={() => {
                if (googleConnected) {
                  googleDisconnect();
                } else {
                  openGoogleAuth(() => checkGoogleStatus());
                }
                setMenuOpen(false);
              }}
              sx={{ py: 2, px: 4 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon icon="Calendar" size={16} sx={{ color: googleConnected ? 'success.main' : 'primary.main' }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="wertBetont">
                  {googleConnected
                    ? `Google Kalender (${googleEmail})`
                    : 'Google Kalender verbinden'}
                </Typography>
              </ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignOut} sx={{ py: 2, px: 4 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon icon="Exit" size={16} sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="wertBetont" sx={{ color: 'error.main' }}>
                  Abmelden
                </Typography>
              </ListItemText>
            </MenuItem>
          </Menu>
          <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
        </Stack>
      </Stack>
    </KopfBand>
  );
};

export default AppBar;
